import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import SelectDropdown from './select';
import SelectDropdownInline from './select-inline';
import MultiSelectDropdownInline from './multi-select-inline';
import { Province, Trade } from './contractor-management/types';
import GeneralContractorDialogs from './general-contractor-management/GeneralContractorDialogs';
import { GeneralContractorFormData, FormErrors as GeneralContractorFormErrors, initialFormData } from './general-contractor-management/types';
import { validateStep1 as validateGeneralContractorStep1, validateStep2 as validateGeneralContractorStep2 } from './general-contractor-management/validation';
import { GeneralContractorService } from '../services/general-contractor.service';
import { lookupService } from '../services/lookups.service';
import { toast } from 'react-toastify';
import { getCoordinatesFromAddress } from './contractor-management/utils';
import { getTodayDateString, isDateInFuture, dateToISOAtNoon, extractDateFromISO } from '../utils/dateUtils';

// Email validation regex - Standard RFC 5322 compliant regex without domain restrictions
const EMAIL_REGEX_STRICT = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

// Define types for the component props
interface JobsiteDetailsStep1Props {
  formData: {
    name?: string;
    jobSiteName?: string;
    subsite: string;
    superintendent: string;
    siteContactEmail: string;
    sectors: string[];
    industry: string;
    fundingResource: string;
    activityStatus: string;
    completionStep: string;
    generalContractor?: string;
    markAsComp: boolean;
    hasPLA?: boolean;
    hasMRF?: boolean;
    projectStartDate?: string;
  };
  errors?: {
    name?: string;
    jobSiteName?: string;
    superintendent?: string;
    siteContactEmail?: string;
    sectors?: string;
    industry?: string;
    fundingResource?: string;
    generalContractor?: string;
    projectStartDate?: string;
  };
  tradeOptions: Trade[];
  sectors: Array<{ id: string | number; name: string }>;
  industries: Array<{ id: string | number; name: string; sector: { id: string | number; name: string } }>;
  fundingResources: Array<{ id: string | number; name: string }>;
  contractors: Array<{ id: string | number; name: string }>;
  trades?: Array<{ id: string; name: string; category?: string }>;
  selectedContractor: string;
  completionSteps: Array<{ label: string; value: string }>;
  provinces: Province[];
  isEditMode?: boolean;
  disableCompletionStatus?: boolean;
  completionStatusMessage?: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onContractorSelect: (value: string) => void;
  onAddContractor: () => void;
  onCompletionStepChange: (step: string) => void;
  onActivityStatusChange: (isActive: boolean) => void;
  onSectorsChange: (values: string[]) => void;
  onIndustryChange: (value: string) => void;
  onFundingResourceChange: (value: string) => void;
  onMarkAsCompChange?: (checked: boolean) => void;
  onContractorAdded: (contractorId: string) => void;
  onProjectStartDateChange?: (value: string) => void;
}

const JobsiteDetailsStep1: React.FC<JobsiteDetailsStep1Props> = ({
  formData,
  errors = {},
  tradeOptions,
  sectors,
  industries,
  fundingResources,
  contractors,
  trades = [],
  selectedContractor,
  completionSteps,
  provinces,
  isEditMode = false,
  disableCompletionStatus = false,
  completionStatusMessage,
  onInputChange,
  onContractorSelect,
  onAddContractor,
  onCompletionStepChange,
  onActivityStatusChange,
  onSectorsChange,
  onIndustryChange,
  onFundingResourceChange,
  onMarkAsCompChange,
  onContractorAdded,
  onProjectStartDateChange,
}) => {
  // General Contractor modal state
  const [showAddContractorDialog, setShowAddContractorDialog] = useState(false);
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState<null | (() => void)>(null);
  const [currentContractorStep, setCurrentContractorStep] = useState(1);
  const [isSubmittingContractor, setIsSubmittingContractor] = useState(false);
  const [contractorFormData, setContractorFormData] = useState<GeneralContractorFormData>(initialFormData);
  const [contractorFormErrors, setContractorFormErrors] = useState<GeneralContractorFormErrors>({
    name: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
  });
  const [provinceOptions, setProvinceOptions] = useState<Province[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const addGeneralContractorDialogContentRef = useRef<HTMLDivElement>(null);

  // Load provinces for contractor form
  useEffect(() => {
    const loadProvinces = async () => {
      setIsLoadingProvinces(true);
      try {
        const provinces = await lookupService.getProvinces();
        setProvinceOptions(provinces);
      } catch (error) {
        console.error('Error loading provinces:', error);
      } finally {
        setIsLoadingProvinces(false);
      }
    };
    loadProvinces();
  }, []);

  // General Contractor form handlers
  const handleContractorInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContractorFormData(prev => ({ ...prev, [name]: value }));

    // Clear related errors
    if (contractorFormErrors[name as keyof GeneralContractorFormErrors]) {
      setContractorFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleContractorBack = () => {
    setCurrentContractorStep(prev => prev - 1);
  };

  const handleContractorNext = () => {
    if (currentContractorStep === 1) {
      const isValid = validateGeneralContractorStep1(contractorFormData, setContractorFormErrors);
      if (isValid) {
        setCurrentContractorStep(2);
      }
    } else if (currentContractorStep === 2) {
      const isValid = validateGeneralContractorStep2(contractorFormData, setContractorFormErrors, provinceOptions);
      if (isValid) {
        setShowSaveConfirmDialog(true);
      }
    }
  };

  const confirmSave = async () => {
    setIsSubmittingContractor(true);
    setShowSaveConfirmDialog(false);

    try {
      // Geocode address if provided
      let coordinates: number[] | undefined;
      if (contractorFormData.address && contractorFormData.city && contractorFormData.province && contractorFormData.postalCode) {
        const coords = await getCoordinatesFromAddress(
          contractorFormData.address,
          contractorFormData.city,
          contractorFormData.province,
          contractorFormData.postalCode
        );
        if (coords) {
          coordinates = coords;
        }
      }

      // Prepare data for API - address must be an object
      const apiData: any = {
        name: contractorFormData.name,
        active: contractorFormData.active,
      };

      // Only include address if at least one field is provided
      if (contractorFormData.address || contractorFormData.city || contractorFormData.province || contractorFormData.postalCode) {
        apiData.address = {
          address: contractorFormData.address || null,
          city: contractorFormData.city || null,
          province: contractorFormData.province || null,
          postalCode: contractorFormData.postalCode || null,
          coordinates: coordinates || null
        };
      }

      const response: any = await GeneralContractorService.createGeneralContractor(apiData);
      toast.success('General contractor created successfully');

      // Close modal and reset
      setShowAddContractorDialog(false);
      setCurrentContractorStep(1);
      setContractorFormData(initialFormData);
      setContractorFormErrors({
        name: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
      });

      // Call parent callback with new contractor ID (response.data.id from backend)
      if (response && response.data && response.data.id) {
        onContractorAdded(response.data.id);
      }
    } catch (error: any) {
      console.error('Error creating general contractor:', error);
      toast.error(error.message || 'Failed to create general contractor');
    } finally {
      setIsSubmittingContractor(false);
    }
  };

  const handleCancelSave = () => {
    setShowSaveConfirmDialog(false);
  };

  const confirmBlock = () => {
    // Block functionality not needed for add flow
    setShowBlockDialog(false);
  };

  const handleCloseAddGeneralContractorDialog = () => {
    // Check if form has changes
    const hasChanges = contractorFormData.name !== initialFormData.name ||
      contractorFormData.address !== initialFormData.address ||
      contractorFormData.city !== initialFormData.city ||
      contractorFormData.province !== initialFormData.province ||
      contractorFormData.postalCode !== initialFormData.postalCode ||
      contractorFormData.active !== initialFormData.active;

    if (!hasChanges) {
      setShowAddContractorDialog(false);
      setCurrentContractorStep(1);
      setContractorFormData(initialFormData);
      return;
    }

    // Show confirmation dialog
    setShowCloseConfirmDialog(true);
    setPendingCloseAction(() => () => {
      setShowAddContractorDialog(false);
      setCurrentContractorStep(1);
      setContractorFormData(initialFormData);
      setShowCloseConfirmDialog(false);
    });
  };

  // Determine which field name to use based on isEditMode
  const nameField = isEditMode ? 'jobSiteName' : 'name';
  const nameValue = isEditMode ? formData.jobSiteName : formData.name;
  const nameError = isEditMode ? errors.jobSiteName : errors.name;

  // Filter industries based on selected sectors
  const filteredIndustries = formData.sectors && formData.sectors.length > 0
    ? industries.filter(industry => formData.sectors.includes(industry.sector.id.toString()))
    : [];

  // Reset industry selection when sectors change
  const handleSectorsChange = (values: string[]) => {
    onSectorsChange(values);
    onIndustryChange(''); // Reset industry selection
  };

  // Handle site contact email input with validation
  const handleSiteContactEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // Convert email to lowercase
    const lowercaseEmail = value.toLowerCase();
    
    // Create synthetic event with lowercase email
    const syntheticEvent = {
      target: {
        name: 'siteContactEmail',
        value: lowercaseEmail
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onInputChange(syntheticEvent);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-base sm:text-lg font-medium text-gray-900">Job Details</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
        {/* Jobsite Name Input */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1.5">
            Jobsite Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name={nameField}
            placeholder="Enter jobsite name"
            className={`w-full px-3 py-2 border ${nameError ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm`}
            value={nameValue || ''}
            onChange={onInputChange}
            id="jobsite-name-input"
          />
          {nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
          {nameValue && nameValue.length > 100 && <p className="mt-1 text-xs text-red-500">Jobsite name cannot exceed 100 characters</p>}
        </div>

        {/* Subsite Input */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Subsite (if applicable)
          </label>
          <input
            type="text"
            name="subsite"
            placeholder="Enter subsite"
            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
            value={formData.subsite}
            onChange={onInputChange}
            id="jobsite-subsite-input"
          />
          {formData.subsite.length > 100 && <p className="mt-1 text-xs text-red-500">Subsite cannot exceed 100 characters</p>}
        </div>

        {/* Site Contact/Superintendent Input */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Site Contact/Superintendent
          </label>
          <input
            type="text"
            name="superintendent"
            placeholder="Enter superintendent name"
            className={`w-full px-3 py-2 border ${errors.superintendent ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm`}
            value={formData.superintendent}
            onChange={onInputChange}
            id="superintendent-input"
          />
          {errors.superintendent && <p className="mt-1 text-xs text-red-500">{errors.superintendent}</p>}
          {formData.superintendent.length > 100 && <p className="mt-1 text-xs text-red-500">Superintendent name cannot exceed 100 characters</p>}
        </div>

        {/* Site Contact Email Input */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Site Contact Email
          </label>
          <input
            type="email"
            name="siteContactEmail"
            placeholder="Enter site contact email"
            className={`w-full px-3 py-2 border ${errors.siteContactEmail ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm`}
            value={formData.siteContactEmail}
            onChange={handleSiteContactEmailChange}
            id="site-contact-email-input"
            pattern={EMAIL_REGEX_STRICT.source}
            title="Please enter a valid email address"
            onKeyDown={(e) => {
              // Prevent space key
              if (e.key === ' ') {
                e.preventDefault();
              }
            }}
            onBlur={(e) => {
              // Trim spaces on blur
              if (e.target.value.trim() !== e.target.value) {
                handleSiteContactEmailChange({
                  target: { value: e.target.value.trim() }
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
          />
          {errors.siteContactEmail && <p className="mt-1 text-xs text-red-500">{errors.siteContactEmail}</p>}
          {!errors.siteContactEmail && formData.siteContactEmail && !EMAIL_REGEX_STRICT.test(formData.siteContactEmail) && (
            <p className="mt-1 text-xs text-red-500" data-testid="site-email-validation-error">Please enter a valid email address</p>
          )}
          {formData.siteContactEmail.length > 100 && <p className="mt-1 text-xs text-red-500">Email cannot exceed 100 characters</p>}
        </div>

        {/* Sectors Multi-Select Dropdown */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Sectors <span className="text-red-500">*</span>
          </label>
          <div className={`${errors.sectors ? 'border border-red-500 rounded-md' : ''}`} id="sectors-dropdown-container">
            <MultiSelectDropdownInline
              testId="jobsite-sectors"
              options={sectors}
              getValue={(option) => option.id.toString()}
              getDisplayText={(option) => option.name}
              onSelect={handleSectorsChange}
              selectedValues={formData.sectors}
              placeholder="Select sectors"
              showSelectAll={false}
            />
          </div>
          {errors.sectors && <p className="mt-1 text-xs text-red-500">{errors.sectors}</p>}
        </div>

        {/* Industry Dropdown */}
        <div className={formData.sectors.length === 1 && filteredIndustries.length > 1 ? 'visible' : 'invisible'}>
          <label className="block text-sm text-gray-600 mb-1.5">
            Industry {formData.sectors && formData.sectors.length > 0 && filteredIndustries.length > 0 && <span className="text-red-500">*</span>}
          </label>
          <div className={`${errors.industry ? 'border border-red-500 rounded-md' : ''}`} id="industry-dropdown-container">
            <SelectDropdown
              options={filteredIndustries.map(industry => ({
                value: industry.id.toString(),
                label: industry.name
              }))}
              onSelect={onIndustryChange}
              getValue={(option) => option.value}
              getDisplayText={(option) => option.label}
              selectedValue={formData.industry}
              placeholder={
                !formData.sectors || formData.sectors.length === 0
                  ? "Select sector(s) first"
                  : formData.sectors.length > 1
                  ? "N/A - No industries for selected sector(s)"
                  : "Select industry"
              }
              disabled={Boolean(!formData.sectors || formData.sectors.length === 0 || filteredIndustries.length === 0 || formData.sectors.length > 1)}
            />
          </div>
          {errors.industry && <p className="mt-1 text-xs text-red-500">{errors.industry}</p>}
        </div>

        {/* Funding Resource Dropdown */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Funding Resource
          </label>
          <div className={`${errors.fundingResource ? 'border border-red-500 rounded-md' : ''}`} id="funding-resource-dropdown-container">
            <SelectDropdown
              options={fundingResources.map(resource => ({
                value: resource.id.toString(),
                label: resource.name
              }))}
              onSelect={onFundingResourceChange}
              getValue={(option) => option.value}
              getDisplayText={(option) => option.label}
              selectedValue={formData.fundingResource}
              placeholder="Select funding resource"
            />
          </div>
          {errors.fundingResource && <p className="mt-1 text-xs text-red-500">{errors.fundingResource}</p>}
        </div>

        {/* Project Start Date */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Project Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            className={`w-full px-3 py-2 border ${errors.projectStartDate ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:ring-red-500 focus:border-red-500`}
            value={extractDateFromISO(formData.projectStartDate || '')}
            max={getTodayDateString()}
            onChange={(e) => {
              const selectedDate = e.target.value;

              // Validate: don't allow future dates
              if (isDateInFuture(selectedDate)) {
                toast.warn('Cannot select future dates');
                return;
              }

              // Store date as ISO string at noon UTC to avoid timezone issues
              const dateValue = dateToISOAtNoon(selectedDate);
              if (onProjectStartDateChange) {
                onProjectStartDateChange(dateValue);
              }
            }}
          />
          {errors.projectStartDate && <p className="mt-1 text-xs text-red-500">{errors.projectStartDate}</p>}
          <p className="text-xs text-gray-500 mt-1">
            Select when the project started. This date cannot be in the future.
          </p>
        </div>

        {/* PLA Checkbox */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Project Labor Agreement (PLA)
          </label>
          <div className="flex items-center h-[42px]">
            <input
              type="checkbox"
              name="hasPLA"
              checked={formData.hasPLA || false}
              onChange={onInputChange}
              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              id="has-pla-checkbox"
            />
            <label htmlFor="has-pla-checkbox" className="ml-2 text-sm text-gray-700">
              This jobsite has a PLA
            </label>
          </div>
        </div>

        {/* MRF Checkbox */}
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            Market Recovery Fund (MRF)
          </label>
          <div className="flex items-center h-[42px]">
            <input
              type="checkbox"
              name="hasMRF"
              checked={formData.hasMRF || false}
              onChange={onInputChange}
              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              id="has-mrf-checkbox"
            />
            <label htmlFor="has-mrf-checkbox" className="ml-2 text-sm text-gray-700">
              This jobsite has MRF funding
            </label>
          </div>
        </div>

        {/* Job Completion Steps */}
        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm text-gray-600 mb-3">
            Job Completion
          </label>
          {disableCompletionStatus && completionStatusMessage && (
            <div className="mb-3 p-3 sm:p-4 bg-gray-100 rounded-md">
              <p className="text-sm text-gray-600">{completionStatusMessage}</p>
            </div>
          )}
          <div className={`relative ${disableCompletionStatus ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Progress Line - Horizontal on desktop, vertical on mobile */}
            <div className="hidden md:block absolute h-[3px] bg-gray-200 left-0 right-0 top-[6px] mx-[44px] my-0 ml-[40px]" />
            <div className="md:hidden absolute w-[3px] bg-gray-200 top-0 bottom-0 left-[6px] my-[26px]" />

            {/* Steps */}
            <div className="relative flex flex-col md:flex-row md:justify-between space-y-6 md:space-y-0">
              {completionSteps.map((step) => {
                const isActive = completionSteps
                  .slice(0, completionSteps.findIndex(s => s.value === formData.completionStep) + 1)
                  .map(s => s.value)
                  .includes(step.value);

                return (
                  <div
                    key={step.value}
                    className={`flex md:flex-col items-start md:items-center ${!disableCompletionStatus ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    onClick={() => !disableCompletionStatus && onCompletionStepChange(step.value)}
                  >
                    <div
                      className={`w-[14px] h-[14px] rounded-full z-9 mb-0 md:mb-2
                        ${isActive ? 'bg-[#A42823]' : 'bg-gray-200'}`}
                    />
                    <span className={`text-xs whitespace-pre text-left md:text-center ml-3 md:ml-0
                      ${isActive ? 'text-black' : 'text-gray-400'}`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Assign Contractor Section */}
        <div className="col-span-1 md:col-span-2 mt-4">
          <div className="flex flex-row justify-between items-start sm:items-center mb-4 gap-2">
            <h2 className="text-lg font-medium text-gray-900">General Contractor</h2>
            <button
              onClick={() => setShowAddContractorDialog(true)}
              className="btn-secondary text-[22px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px]"
              id="add-contractor-button"
            >
              <span className=""> + </span>
              <span className="ml-1 hidden sm:inline">
                General Contractor
              </span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex-1 ${errors.generalContractor ? 'border border-red-500 rounded-md' : ''}`} id="contractor-dropdown-container">
              <SelectDropdownInline
                options={contractors.map(contractor => ({
                  value: contractor.id.toString(),
                  label: contractor.name
                }))}
                onSelect={onContractorSelect}
                getValue={(option) => option.value}
                getDisplayText={(option) => option.label}
                selectedValue={selectedContractor}
                placeholder="Select contractor"
                testId="contractor-select"
                showSearch={true}
                searchPlaceholder="Search contractors..."
              />
            </div>
            <button
              type="button"
              onClick={() => onContractorSelect('')}
              disabled={!selectedContractor}
              className={`p-2 rounded-md transition-colors ${
                selectedContractor
                  ? 'hover:bg-gray-100 cursor-pointer'
                  : 'cursor-not-allowed opacity-40'
              }`}
              title={selectedContractor ? 'Clear selection' : 'No contractor selected'}
              aria-label="Clear contractor selection"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {errors.generalContractor && <p className="mt-1 text-xs text-red-500">{errors.generalContractor}</p>}
        </div>
      </div>

      {/* Add General Contractor Dialog */}
      <GeneralContractorDialogs
        showAddGeneralContractorDialog={showAddContractorDialog}
        showBlockDialog={showBlockDialog}
        showSaveConfirmDialog={showSaveConfirmDialog}
        showCloseConfirmDialog={showCloseConfirmDialog}
        isSubmitting={isSubmittingContractor}
        formData={contractorFormData}
        errors={contractorFormErrors}
        currentStep={currentContractorStep}
        editFormData={null}
        addGeneralContractorDialogContentRef={addGeneralContractorDialogContentRef}
        provinceOptions={provinceOptions}
        isLoadingProvinces={isLoadingProvinces}
        handleCloseAddGeneralContractorDialog={handleCloseAddGeneralContractorDialog}
        handleInputChange={handleContractorInputChange}
        handleBack={handleContractorBack}
        handleNext={handleContractorNext}
        handleCancelSave={handleCancelSave}
        confirmSave={confirmSave}
        confirmBlock={confirmBlock}
        setShowBlockDialog={setShowBlockDialog}
        setShowCloseConfirmDialog={setShowCloseConfirmDialog}
        pendingCloseAction={pendingCloseAction}
        setFormData={setContractorFormData}
        hideStatusToggle={true}
      />
    </div>
  );
};

export default JobsiteDetailsStep1; 